import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ContentAssignment {
  id: string;
  chapter_id: string;
  chapter_name: string;
  program_id: string;
  program_name: string;
  program_status: string | null;
  is_mandatory: boolean;
  sort_order: number;
}

export interface ProgramWithChapters {
  id: string;
  name: string;
  status: string | null;
  chapters: { id: string; name: string; sort_order: number | null }[];
}

export function useContentAssignments(contentId: string | null | undefined) {
  return useQuery({
    queryKey: ['content-assignments', contentId],
    enabled: !!contentId,
    queryFn: async (): Promise<ContentAssignment[]> => {
      const { data, error } = await (supabase as any)
        .from('chapter_contents')
        .select(
          'id, chapter_id, is_mandatory, sort_order, program_chapters!inner(id, name, sort_order, program_id, programs!inner(id, name, status))'
        )
        .eq('content_id', contentId);
      if (error) throw error;

      return ((data ?? []) as any[])
        .map((row) => ({
          id: row.id,
          chapter_id: row.chapter_id,
          chapter_name: row.program_chapters?.name ?? '',
          program_id: row.program_chapters?.programs?.id ?? '',
          program_name: row.program_chapters?.programs?.name ?? '',
          program_status: row.program_chapters?.programs?.status ?? null,
          is_mandatory: !!row.is_mandatory,
          sort_order: row.sort_order ?? 0,
        }))
        .sort(
          (a, b) =>
            a.program_name.localeCompare(b.program_name) ||
            a.chapter_name.localeCompare(b.chapter_name) ||
            a.sort_order - b.sort_order
        );
    },
  });
}

export function useProgramsWithChapters() {
  return useQuery({
    queryKey: ['programs-with-chapters'],
    queryFn: async (): Promise<ProgramWithChapters[]> => {
      const [progRes, chRes] = await Promise.all([
        (supabase as any).from('programs').select('id, name, status').order('name'),
        (supabase as any)
          .from('program_chapters')
          .select('id, name, sort_order, program_id')
          .order('sort_order'),
      ]);
      if (progRes.error) throw progRes.error;
      if (chRes.error) throw chRes.error;

      return ((progRes.data ?? []) as any[]).map((p) => ({
        id: p.id,
        name: p.name,
        status: p.status ?? null,
        chapters: ((chRes.data ?? []) as any[])
          .filter((c) => c.program_id === p.id)
          .map((c) => ({ id: c.id, name: c.name, sort_order: c.sort_order ?? 0 })),
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidate(contentId: string | null | undefined) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['content-assignments', contentId] });
    qc.invalidateQueries({ queryKey: ['content-programs', contentId] });
    qc.invalidateQueries({ queryKey: ['program-detail'] });
    qc.invalidateQueries({ queryKey: ['content-bank'] });
  };
}

export function useAddContentAssignment(contentId: string) {
  const invalidate = useInvalidate(contentId);
  return useMutation({
    mutationFn: async ({
      chapterId,
      isMandatory,
      sortOrder,
    }: {
      chapterId: string;
      isMandatory: boolean;
      sortOrder?: number | null;
    }) => {
      // Evitar duplicados
      const { data: existing, error: exErr } = await (supabase as any)
        .from('chapter_contents')
        .select('id')
        .eq('chapter_id', chapterId)
        .eq('content_id', contentId)
        .maybeSingle();
      if (exErr) throw exErr;
      if (existing) throw new Error('Este contenido ya está asignado a ese capítulo');

      let order = sortOrder;
      if (order === undefined || order === null || Number.isNaN(order)) {
        const { data: rows } = await (supabase as any)
          .from('chapter_contents')
          .select('sort_order')
          .eq('chapter_id', chapterId)
          .order('sort_order', { ascending: false })
          .limit(1);
        order = ((rows?.[0]?.sort_order ?? -1) as number) + 1;
      }

      const { data, error } = await (supabase as any)
        .from('chapter_contents')
        .insert({
          chapter_id: chapterId,
          content_id: contentId,
          is_mandatory: isMandatory,
          sort_order: order,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateContentAssignment(contentId: string) {
  const invalidate = useInvalidate(contentId);
  return useMutation({
    mutationFn: async ({
      id,
      is_mandatory,
      sort_order,
    }: {
      id: string;
      is_mandatory?: boolean;
      sort_order?: number;
    }) => {
      const updates: any = {};
      if (is_mandatory !== undefined) updates.is_mandatory = is_mandatory;
      if (sort_order !== undefined) updates.sort_order = sort_order;
      const { error } = await (supabase as any)
        .from('chapter_contents')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: invalidate,
  });
}

export function useRemoveContentAssignment(contentId: string) {
  const invalidate = useInvalidate(contentId);
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('chapter_contents').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: invalidate,
  });
}
